import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Pencil, Trash2, X, Factory } from "lucide-react";
import { adminApi, type MakerRow, type MakerInput } from "@/lib/api";

const EMPTY: MakerInput = { name: "", contact: "", note: "" };

export function MakersPage() {
  const qc = useQueryClient();
  const { data: makers, isLoading, error } = useQuery({ queryKey: ["makers"], queryFn: () => adminApi.listMakers() });
  const [editing, setEditing] = useState<MakerRow | "new" | null>(null);

  const del = useMutation({
    mutationFn: (id: string) => adminApi.deleteMaker(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["makers"] }),
  });

  return (
    <div className="px-4 py-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">メーカー管理（仕入先）</h2>
        <button
          onClick={() => setEditing("new")}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-brand-500 text-white text-sm font-semibold"
        >
          <Plus size={15} /> 追加
        </button>
      </div>
      <p className="text-xs text-ink-soft dark:text-white/60">
        エアコン本体の仕入先（メーカー）を登録します。発注メールの宛先・連絡先の管理に使用します。
      </p>

      {isLoading && <p className="text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> 読み込み中...</p>}
      {error && <p className="text-sm text-red-600">読み込みに失敗しました</p>}
      {makers && makers.length === 0 && (
        <p className="text-sm text-ink-soft">仕入先がまだ登録されていません。右上「追加」から登録してください。</p>
      )}

      <ul className="space-y-2">
        {makers?.map((m) => (
          <li key={m.id} className="rounded-xl border border-[var(--color-border)] p-3 sm:p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Factory size={16} className="text-brand-500 shrink-0" />
                  <span className="font-semibold truncate">{m.name}</span>
                </div>
                {m.contact && <p className="text-xs text-ink-soft dark:text-white/60 mt-1">連絡先：{m.contact}</p>}
                {m.note && <p className="text-xs text-ink-soft dark:text-white/50 mt-0.5 whitespace-pre-wrap">{m.note}</p>}
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => setEditing(m)} aria-label="編集" className="p-2 rounded-lg border border-[var(--color-border)]">
                  <Pencil size={14} />
                </button>
                <button
                  onClick={() => { if (confirm(`「${m.name}」を削除しますか？`)) del.mutate(m.id); }}
                  aria-label="削除"
                  className="p-2 rounded-lg border border-red-300 text-red-600 dark:text-red-300"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {editing && (
        <MakerEditor
          maker={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["makers"] }); }}
        />
      )}
    </div>
  );
}

function MakerEditor({ maker, onClose, onSaved }: { maker: MakerRow | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<MakerInput>(
    maker ? { name: maker.name, contact: maker.contact ?? "", note: maker.note ?? "" } : EMPTY,
  );
  const save = useMutation({
    mutationFn: () => (maker ? adminApi.updateMaker(maker.id, form) : adminApi.createMaker(form)),
    onSuccess: onSaved,
  });
  const canSave = form.name.trim().length > 0 && !save.isPending;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-6">
      <div className="bg-[var(--color-bg)] w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-bold">{maker ? "仕入先を編集" : "仕入先を追加"}</h3>
          <button onClick={onClose} className="p-1 text-ink-soft"><X size={18} /></button>
        </div>
        <label className="block space-y-1">
          <span className="text-xs text-ink-soft">メーカー名 / 仕入先名 <span className="text-red-500">*</span></span>
          <input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="例：ダイキン工業株式会社"
            className="w-full text-sm border border-[var(--color-border)] rounded-md p-2 bg-transparent"
          />
        </label>
        <label className="block space-y-1">
          <span className="text-xs text-ink-soft">連絡先（メール / 電話）</span>
          <input
            value={form.contact ?? ""}
            onChange={(e) => setForm((f) => ({ ...f, contact: e.target.value }))}
            placeholder="例：order@example.co.jp"
            className="w-full text-sm border border-[var(--color-border)] rounded-md p-2 bg-transparent"
          />
        </label>
        <label className="block space-y-1">
          <span className="text-xs text-ink-soft">備考</span>
          <textarea
            value={form.note ?? ""}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            rows={3}
            placeholder="担当者名・取引条件・納期目安など"
            className="w-full text-sm border border-[var(--color-border)] rounded-md p-2 bg-transparent"
          />
        </label>
        {save.error && <p className="text-xs text-red-600">{(save.error as Error).message}</p>}
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="flex-1 py-2 rounded-lg border border-[var(--color-border)] text-sm">キャンセル</button>
          <button
            onClick={() => save.mutate()}
            disabled={!canSave}
            className="flex-1 py-2 rounded-lg bg-brand-500 text-white text-sm font-semibold disabled:opacity-50"
          >
            {save.isPending ? "保存中..." : "保存"}
          </button>
        </div>
      </div>
    </div>
  );
}
